import { ReactNode, useCallback, useMemo, useState } from 'react';

import {
  Divider,
  HStack,
  Text,
  VStack,
  Box,
  Flex,
  Button,
  useDisclosure,
} from '@chakra-ui/react';
import { Trans, useTranslation } from 'react-i18next';

import { ErrorIndicator } from 'components/ErrorIndicator';
import {
  AlertTriangleIcon,
  ArrowRightIcon,
  Card,
  CloudTransferIcon,
} from 'components/uikit';
import { OneXbetLogo } from 'components/uikit/icons';
import { AlfaIcon } from 'components/uikit/icons/Alfa';
import { chatwootIcon as ChatwootIcon } from 'components/uikit/icons/chatwoot';
import { RiobetLogo } from 'components/uikit/icons/RiobetLogo';
import { DOMAIN } from 'data/constants';
import {
  DepositBank,
  isDeeplinkMethod,
  isMobcomMethod,
  isQRMethod,
  vndBankSwiftCodes,
} from 'data/payments/models';
import { AnalyticsEvents, InputType, trackEvent } from 'utils/analytics';
import { deepLinks, getDeeplinks } from 'utils/bankDeepLinks';
import { BrowserInfo } from 'utils/browserInfo';
import { useIsMobile } from 'utils/hooks/screenBreakpoints';
import { safeLs } from 'utils/localStorage';
import { openDeeplink } from 'utils/openDeeplink';

import {
  BankDeeplinkButtons,
  ElqrDeeplinkButtons,
  UpiDeeplinkButtons,
} from './BankDeeplinkButtons';
import { BankDeeplinkQR, downloadQrAsPng } from './BankDeeplinkQR';
import { BraTgQrErrorBanner } from './BraTgQrErrorBanner';
import { IdrWalletInstructions } from './IdrWalletInstructions';
import { PaymentClientControlsDefault } from './PaymentClientControlsDefault';
import { PaymentCredentialsBaseProps } from './PaymentCredentials';
import { PaymentFieldRow } from './PaymentFieldRow';
import { PaymentInstructionsModal } from './PaymentInstructionsModal';
import { PaymentNotice } from './PaymentNotice';
import { PaymentPageQr } from './PaymentPageQr';
import { PaymentStatusControls } from './PaymentStatusControls';
import { PaymentUploadModal } from './PaymentUploadModal';
import { PhpTgQrErrorBanner } from './PhpTgQrErrorBanner';
import { VndAnInstructions } from './VndAnInstructions';
import { VndTgQrErrorBanner } from './VndTgQrErrorBanner';

interface PaymentCredentialsDefaultProps extends PaymentCredentialsBaseProps {
  style?: 'default' | 'riobet' | '1xbet';
  crossborderAmount: string | null;
  crossborderCurrency: string | null;
  onInputCopied: (inputName: InputType) => void;
  showChatwoot?: boolean;
}

export function PaymentCredentialsDefault({
  id,
  acquirer,
  method,
  amount,
  currency,
  credentials: rawCredentials,
  failureRedirectLink,
  fromBank,
  status,
  timeLeft,
  paid,
  onPaid,
  onCancel,
  onInputCopied,
  onUploadClick,
  onUploadComplete,
  isUploadLoading,
  uploadModalIsOpen,
  uploadModalOnClose,
  style = 'default',
  crossborderAmount,
  crossborderCurrency,
  showChatwoot,
}: PaymentCredentialsDefaultProps) {
  const { t } = useTranslation('payments');
  // QR credentials are not ready until postprocessing is done
  const credentials = useMemo(
    () =>
      rawCredentials.postProcessStatus && method && isQRMethod(method)
        ? { ...rawCredentials, type: 'QR' as const, value: null }
        : rawCredentials,
    [rawCredentials, method],
  );
  const isRiobet = style === 'riobet';
  const isOneXbet = style === '1xbet';
  const isBranded = isRiobet || isOneXbet;
  const isMobile = useIsMobile();
  const isAndroid = isMobile && BrowserInfo.mobileOS === 'android';
  const instructionsDisclosure = useDisclosure();
  const isUpi = method === 'INR';
  const isPix = method === 'PIX';
  const isVndAn = method === 'VND_TG_AN';
  // These methods are paid in rubles, the guide shows the ruble amount
  const isRubGuide = isVndAn || method === 'IDR_TG_WALLET';
  const showInstructions =
    !isUpi &&
    !isPix &&
    // VND_TG_AN screenshots exist only for Sber
    !(isVndAn && fromBank !== 'Сбербанк') &&
    method !== 'KGS_SBP' &&
    method !== 'KGS_ELQR';

  const hasDeeplinks = (() => {
    if (!method || !fromBank || !isMobile) return false;
    const mobileOS = BrowserInfo.mobileOS;
    if (!mobileOS) return false;
    const bank = fromBank as DepositBank;
    const bankLinks = deepLinks[mobileOS]?.[bank]?.[method];
    return !!bankLinks && Object.keys(bankLinks).length > 0;
  })();

  const credentialLabel = t(
    isUpi
      ? 'payment_upi_id'
      : isPix
      ? 'payment_pix_code'
      : credentials.type === 'SBP'
      ? 'payment_phone_number'
      : 'payment_card_number',
  );

  // Any UPI app pays a scanned upi:// link to the trader's VPA with the amount prefilled
  const upiPayLink =
    isUpi && credentials.value
      ? `upi://pay?${new URLSearchParams({
          pa: credentials.value,
          am: amount,
          cu: 'INR',
        }).toString()}`
      : undefined;

  const credentialCopyValue =
    isMobile && method === 'MOBCOM_TJS' && credentials.value?.startsWith('+')
      ? credentials.value.replace(/^\+992/, '')
      : credentials.value ?? '';

  const isVietnam = method === 'VND_TG_QR' || method === 'IDR_TG_QR';
  const isPhilippines = method === 'PHP_TG_QR';
  const isBrazil = method === 'BRA_TG_QR';
  // Crossborder methods share the compact single-column layout
  const isCrossborderLayout = isVietnam || isPhilippines || isBrazil || isVndAn;

  const alfaTjsDeeplink =
    isAndroid && method === 'TJS_TG' && fromBank === 'Альфа-Банк'
      ? getDeeplinks(
          method,
          credentials.type,
          fromBank as DepositBank,
          amount,
          credentials.value ?? '',
          credentials.bank,
          'android',
        )[0]
      : undefined;
  const gazpromTjsDeeplink =
    method === 'TJS_TG' && fromBank === 'Газпромбанк'
      ? getDeeplinks(
          method,
          credentials.type,
          fromBank as DepositBank,
          amount,
          credentials.value ?? '',
          credentials.bank,
          'android',
        )[0]
      : undefined;
  const isAlfaVnd = isVietnam && fromBank === 'Альфа-Банк';
  const isSimpleLayout =
    fromBank === 'ВТБ' ||
    fromBank === 'МТС Банк' ||
    fromBank === 'Газпромбанк' ||
    isAlfaVnd;

  const qrUrlPrefix =
    isVietnam || isVndAn
      ? 'vnd'
      : isPhilippines
      ? 'php'
      : isBrazil
      ? 'bra'
      : '';

  const instructionsPrefix = 'vnd';

  const QR_BANK_CONFIG: Record<
    string,
    {
      payImageUrl: string;
      payLogo?: ReactNode;
      payLink: string;
      alt: string;
      fileInstructionImageUrl?: string;
      fileInstructionImageWidth?: string;
      fileInstructionImageRadius?: string;
      buttonNameKey: string;
      qrImageUrl: string;
    }
  > = {
    Сбербанк: {
      payImageUrl: `https://payment.${DOMAIN}/assets/${qrUrlPrefix}/sber-pay.png`,
      payLink: 'https://www.sberbank.ru/sms/qr',
      alt: 'Sber Pay',
      fileInstructionImageUrl: `https://payment.${DOMAIN}/assets/${qrUrlPrefix}/sber/file-instruction.png`,
      buttonNameKey: `${qrUrlPrefix}_button_name_sber`,
      qrImageUrl: `https://payment.${DOMAIN}/assets/${qrUrlPrefix}/sber/qr.png`,
    },
    ВТБ: {
      payImageUrl: `https://payment.${DOMAIN}/assets/${qrUrlPrefix}/vtb-pay.png`,
      payLink: 'https://online.vtb.ru/i/qrmain',
      alt: 'VTB Pay',
      fileInstructionImageUrl: `https://payment.${DOMAIN}/assets/${qrUrlPrefix}/vtb/file_instruction.png`,
      buttonNameKey: `${qrUrlPrefix}_button_name_vtb`,
      qrImageUrl: `https://payment.${DOMAIN}/assets/${qrUrlPrefix}/sber/qr.png`,
    },
    'МТС Банк': {
      payImageUrl: `https://payment.${DOMAIN}/assets/vnd/mts-pay.png`,
      payLink: 'mymts://action/oplata_qr',
      alt: 'MTS Pay',
      fileInstructionImageUrl: `https://payment.${DOMAIN}/assets/vnd/mts/file-instruction.png`,
      buttonNameKey: `${qrUrlPrefix}_button_name_mts`,
      qrImageUrl: `https://payment.${DOMAIN}/assets/vnd/sber/qr.png`,
    },
    Газпромбанк: {
      payImageUrl: `https://payment.${DOMAIN}/assets/vnd/gazprom-pay.png`,
      payLink: 'gpbapp://payments/qrcode',
      alt: 'Gazprombank Pay',
      fileInstructionImageUrl: `https://payment.${DOMAIN}/assets/vnd/gazprom/file-instruction.png`,
      buttonNameKey: `${qrUrlPrefix}_button_name_gazprom`,
      qrImageUrl: `https://payment.${DOMAIN}/assets/vnd/sber/qr.png`,
    },
    'Альфа-Банк': {
      payImageUrl: `https://payment.${DOMAIN}/assets/vnd/alfa-pay.png`,
      payLogo: (
        <Flex
          w='100%'
          h='36px'
          alignItems='center'
          justifyContent='center'
          bg='#EE2A23'
          borderRadius='8px'
          px='4px'
        >
          <AlfaIcon w='100%' h='100%' />
        </Flex>
      ),
      payLink: 'alfabank://qr_payment',
      alt: 'Alfa-Bank Pay',
      fileInstructionImageUrl: `https://payment.${DOMAIN}/assets/vnd/alfa/file-instruction.png`,
      fileInstructionImageRadius: '8px',
      buttonNameKey: `${qrUrlPrefix}_button_name_alfa`,
      qrImageUrl: `https://payment.${DOMAIN}/assets/vnd/sber/qr.png`,
    },
  };

  const bankConfig = fromBank ? QR_BANK_CONFIG[fromBank] : undefined;
  // Opens Sber straight on the transfer abroad screen, the scheme works on Android only.
  // The link is one-off: it is hidden for this payment after the first tap
  const vndAnBankLinkStorageKey = `@vnd_an_bank_link_used/${id}`;
  const [isVndAnBankLinkUsed, setIsVndAnBankLinkUsed] = useState(() => {
    try {
      return safeLs?.getItem(vndAnBankLinkStorageKey) === 'true';
    } catch {
      return false;
    }
  });
  const vndAnBankLink =
    isVndAn && isAndroid && fromBank === 'Сбербанк' && !isVndAnBankLinkUsed
      ? 'android-app://ru.sberbankmobile/transfers/overseas'
      : undefined;

  // VND_TG_AN is branded only for Sber so far
  const vndAnBankConfig =
    isVndAn && fromBank === 'Сбербанк' ? bankConfig : undefined;
  const brandConfig = isVndAn
    ? vndAnBankConfig
    : isVietnam || isPhilippines || isBrazil
    ? bankConfig
    : undefined;

  const openBankPayLink = useCallback(() => {
    if (!bankConfig?.payLink) return;
    trackEvent(AnalyticsEvents.PAYMENT_BANK_PAY_LINK_CLICKED, {
      order_id: id,
      payment_method: method ?? '',
      amount_base: amount,
      bank: fromBank ?? undefined,
    });
    window.open(bankConfig.payLink, '_blank');
  }, [bankConfig, id, method, amount, fromBank]);

  const qrErrorImageUrl = isAlfaVnd
    ? `https://payment.${DOMAIN}/assets/vnd/alfa/errorImage.png`
    : `https://payment.${DOMAIN}/assets/${qrUrlPrefix}/errorImage.png`;

  const handleSaveQr = useCallback(() => {
    if (!credentials.value) return;
    trackEvent(AnalyticsEvents.PAYMENT_QR_SAVED, {
      order_id: id,
      payment_method: method ?? '',
      amount_base: amount,
      bank: fromBank ?? undefined,
    });
    const params = isAndroid
      ? {
          canvasWidth: 500,
          canvasHeight: 1000,
          qrWidthRatio: 0.7,
          qrHeightRatio: 0.7,
          offsetXRatio: 0.17,
          offsetYRatio: 0.42,
        }
      : {
          canvasWidth: 1000,
          canvasHeight: 1000,
          qrWidthRatio: 0.25,
          qrHeightRatio: 0.25,
          offsetXRatio: 0.07,
          offsetYRatio: 0.3,
        };
    downloadQrAsPng(credentials.value, `qr-${id}.png`, method ?? 'VND_TG_QR', {
      ...params,
      backgroundColor: isAlfaVnd ? '#1C1C1E' : undefined,
      center: isAlfaVnd,
    });
  }, [credentials.value, id, method, amount, fromBank, isAndroid, isAlfaVnd]);

  const renderControls = () => {
    if (acquirer === 'TEST') return <PaymentStatusControls id={id} />;
    return (
      <PaymentClientControlsDefault
        id={id}
        failureRedirectLink={failureRedirectLink}
        onPaid={onPaid}
        onCancel={onCancel}
      />
    );
  };

  const content = (
    <VStack
      spacing={['0', '24px']}
      align='start'
      w='100%'
      pt={['0', '0']}
      zIndex={1}
    >
      {/* Brand Logo */}
      {isRiobet ? (
        <Box
          w='100%'
          display='flex'
          justifyContent='center'
          pt={['20px', '0']}
          pb={['20px', '30px']}
          flexShrink={0}
        >
          <RiobetLogo w='175px' h='56px' />
        </Box>
      ) : isOneXbet ? (
        <HStack
          bgColor='#1C4268'
          alignItems='center'
          justifyContent='center'
          w={['100vw', '100%']}
          maxW={['none', isCrossborderLayout ? '550px' : '900px']}
          mx={['calc(-50vw + 50%)', '0']}
          py={['16px', '20px']}
          borderRadius={['0', '8px']}
        >
          <OneXbetLogo
            color='white'
            w={['80px', '100px']}
            h={['18px', '22px']}
          />
        </HStack>
      ) : null}
      <Card
        bg={['transparent', 'grey.white']}
        boxShadow='none'
        p={['20px', '20px']}
        w={['100vw', '100%']}
        h={['auto', 'auto']}
        maxW={['none', isCrossborderLayout ? '550px' : '900px']}
        borderWidth={['0', '1px']}
        borderColor={['transparent', 'grey.border']}
        borderRadius={['0', '8px']}
        mx={['calc(-50vw + 50%)', '0']}
        my={['0', '0']}
        pb={['20px', '20px']}
        backdropFilter='blur(14px)'
        position='relative'
      >
        {!isMobile && brandConfig ? (
          brandConfig.payLogo ? (
            <Box
              position='absolute'
              top='12px'
              left='12px'
              w='80px'
              zIndex={10}
            >
              {brandConfig.payLogo}
            </Box>
          ) : (
            <Box
              as='img'
              src={brandConfig.payImageUrl}
              alt={brandConfig.alt}
              position='absolute'
              top='12px'
              left='12px'
              w='80px'
              h='auto'
              zIndex={10}
            />
          )
        ) : null}
        {isCrossborderLayout ? (
          <VStack
            w='100%'
            align='stretch'
            spacing={['16px', '0']}
            justify={['space-between', 'space-between']}
            minH={['auto', '100%']}
          >
            <Box>
              <VStack
                pt={['0', '12px']}
                gap={['0px', '0px']}
                w='100%'
                mb={['16px', '20px']}
              >
                <Text
                  as='h3'
                  fontSize={['18px', '20px']}
                  fontWeight='medium'
                  variant='numeric'
                  color='payment.titleText'
                  lineHeight='1'
                >
                  {t('payment_credentials_title')}
                </Text>

                <Text
                  as='h1'
                  fontSize={['56px', '64px']}
                  fontWeight='medium'
                  variant='numeric'
                  color='grey.primaryText'
                  lineHeight='1'
                >
                  {amount}
                  <sub style={{ bottom: 0 }}>{currency}</sub>
                </Text>
                <Text
                  fontWeight='200'
                  fontSize='md'
                  color='payment.titleText'
                  lineHeight='1'
                >
                  {t(`${instructionsPrefix}_tg_qr_comission_warning`)}
                </Text>
              </VStack>

              {credentials.type === 'QR' && (
                <Box
                  w='100%'
                  h='auto'
                  p={[isBranded ? '12px' : '0px', '20px']}
                  bg={['transparent', 'payment.qrBackground']}
                  borderWidth={isBranded ? '1px' : '0'}
                  borderColor='payment.qrBorder'
                  borderRadius='13px'
                  mx='auto'
                  mb={'20px'}
                  position='relative'
                >
                  <Flex
                    flexDirection={['column', 'row']}
                    justifyContent={['center', 'space-between']}
                    alignItems={['center', 'center']}
                    textAlign='center'
                    gap={['20px', '30px']}
                  >
                    {isMobile ? (
                      <>
                        {hasDeeplinks ? (
                          <BankDeeplinkButtons
                            amount={amount}
                            fromBank={fromBank ?? null}
                            method={method}
                            credentials={credentials}
                            id={id}
                          />
                        ) : null}
                        <Box w='100%' display='flex' justifyContent='center'>
                          <BankDeeplinkQR
                            id={id}
                            fromBank={fromBank ?? null}
                            method={method}
                            value={credentials.value}
                            postProcessStatus={credentials.postProcessStatus}
                          />
                        </Box>
                        <Card
                          w='100%'
                          bg={
                            isBranded
                              ? 'transparent'
                              : 'rgba(255, 255, 255, 0.5)'
                          }
                          p='16px'
                          borderRadius='12px'
                          boxShadow='none'
                          borderWidth={isBranded ? '0' : '1px'}
                          borderColor='gray.300'
                        >
                          <VStack
                            textAlign='start'
                            alignItems='stretch'
                            w='100%'
                            spacing='8px'
                          >
                            <HStack
                              w='100%'
                              justifyContent='space-between'
                              alignItems='center'
                              gap='12px'
                            >
                              <Text
                                fontWeight='500'
                                fontSize='md'
                                color='grey.primaryText'
                                lineHeight='1.4'
                                flex='1'
                              >
                                1.{' '}
                                <Trans
                                  ns='payments'
                                  i18nKey={
                                    isAndroid
                                      ? `payment_guide_${instructionsPrefix}_step_1_mobile_v2`
                                      : `payment_guide_${instructionsPrefix}_step_1_mobile`
                                  }
                                  components={{
                                    bold: <Text as='span' fontWeight='700' />,
                                  }}
                                />
                              </Text>
                              {isAndroid ? (
                                <Button
                                  onClick={handleSaveQr}
                                  variant='paymentOutline'
                                  h='36px'
                                  px='14px'
                                  borderRadius='8px'
                                  fontSize='sm'
                                  fontWeight='600'
                                  flexShrink={0}
                                >
                                  {t(
                                    `payment_guide_${instructionsPrefix}_save_qr`,
                                  )}
                                </Button>
                              ) : null}
                            </HStack>
                            <HStack
                              w='100%'
                              justifyContent='space-between'
                              alignItems='center'
                              gap='10px'
                            >
                              <Text
                                fontWeight='500'
                                fontSize='md'
                                color='grey.primaryText'
                                lineHeight='1.4'
                                flex='1'
                              >
                                2.{' '}
                                <Trans
                                  ns='payments'
                                  i18nKey={`payment_guide_${instructionsPrefix}_step_2_mobile_v2`}
                                  components={{
                                    bold: <Text as='span' fontWeight='700' />,
                                  }}
                                />
                              </Text>
                              <ArrowRightIcon
                                color='grey.primaryText'
                                boxSize='20px'
                                flexShrink={0}
                              />
                              {bankConfig ? (
                                <Button
                                  onClick={openBankPayLink}
                                  variant='unstyled'
                                  p='0'
                                  h='auto'
                                  w='auto'
                                  minW='auto'
                                  display='flex'
                                  alignItems='center'
                                  flexShrink={0}
                                  _hover={{ opacity: 0.8 }}
                                  _active={{ opacity: 0.6 }}
                                  _focus={{ boxShadow: 'none' }}
                                >
                                  {bankConfig.payLogo ? (
                                    <Box w='70px'>{bankConfig.payLogo}</Box>
                                  ) : (
                                    <Box
                                      as='img'
                                      src={bankConfig.payImageUrl}
                                      alt={bankConfig.alt}
                                      w='70px'
                                      h='auto'
                                    />
                                  )}
                                </Button>
                              ) : null}
                            </HStack>
                            <HStack
                              w='100%'
                              justifyContent='space-between'
                              alignItems='center'
                              gap='10px'
                            >
                              <Text
                                fontWeight='500'
                                fontSize='md'
                                color='grey.primaryText'
                                lineHeight='1.4'
                                flex='1'
                              >
                                3.{' '}
                                <Trans
                                  ns='payments'
                                  i18nKey={
                                    isAndroid
                                      ? `payment_guide_${instructionsPrefix}_step_3_mobile_v2`
                                      : `payment_guide_${instructionsPrefix}_step_3_mobile`
                                  }
                                  values={{
                                    buttonName: bankConfig
                                      ? t(bankConfig.buttonNameKey)
                                      : '',
                                  }}
                                  components={{
                                    bold: <Text as='span' fontWeight='700' />,
                                  }}
                                />
                              </Text>
                              {bankConfig?.fileInstructionImageUrl ? (
                                <Box
                                  as='img'
                                  src={bankConfig.fileInstructionImageUrl}
                                  alt='File instruction'
                                  w={
                                    bankConfig.fileInstructionImageWidth ??
                                    '120px'
                                  }
                                  h='auto'
                                  borderRadius={
                                    bankConfig.fileInstructionImageRadius
                                  }
                                  flexShrink={0}
                                />
                              ) : null}
                            </HStack>
                            {isVietnam &&
                            (fromBank === 'Сбербанк' || isAlfaVnd) ? (
                              <HStack
                                w='100%'
                                justifyContent='space-between'
                                alignItems='center'
                                gap='10px'
                              >
                                <Text
                                  fontWeight='500'
                                  fontSize='md'
                                  color='grey.primaryText'
                                  lineHeight='1.4'
                                  flex='1'
                                >
                                  4.{' '}
                                  <Trans
                                    ns='payments'
                                    i18nKey={
                                      isAlfaVnd
                                        ? `payment_guide_${instructionsPrefix}_alfa_step_4_mobile`
                                        : `payment_guide_${instructionsPrefix}_step_4_mobile_v3`
                                    }
                                    components={{
                                      bold: <Text as='span' fontWeight='700' />,
                                    }}
                                  />
                                </Text>
                                <ArrowRightIcon
                                  color='grey.primaryText'
                                  boxSize='20px'
                                  flexShrink={0}
                                />
                                <Box
                                  as='img'
                                  src={qrErrorImageUrl}
                                  alt='Error illustration'
                                  w={isAlfaVnd ? '120px' : '90px'}
                                  h='auto'
                                  borderRadius={isAlfaVnd ? '9px' : '8px'}
                                  flexShrink={0}
                                />
                              </HStack>
                            ) : null}
                          </VStack>
                        </Card>
                      </>
                    ) : (
                      <>
                        <VStack
                          textAlign='start'
                          alignItems='start'
                          w={['calc(100% - 120px)', '70%']}
                          maxW={['none', 'none']}
                          flex='1'
                          spacing={['8px', '0']}
                          gap={['8px', '0']}
                        >
                          <Text
                            fontWeight='400'
                            fontSize={['md', 'xl']}
                            color='grey.primaryText'
                            lineHeight='1.4'
                          >
                            1.{' '}
                            <Trans
                              ns='payments'
                              i18nKey={`payment_guide_${instructionsPrefix}_step_1_desktop`}
                              values={{
                                bank: fromBank
                                  ? t(`bank_name_${fromBank}`)
                                  : '',
                              }}
                              components={{
                                bold: <Text as='span' fontWeight='900' />,
                              }}
                            />
                          </Text>
                          <HStack w='100%' gap='15px' position='relative'>
                            <Text
                              fontWeight='400'
                              fontSize={['md', 'xl']}
                              color='grey.primaryText'
                              lineHeight='1.4'
                            >
                              2.{' '}
                              <Trans
                                ns='payments'
                                i18nKey={
                                  fromBank === 'ВТБ'
                                    ? `payment_guide_${instructionsPrefix}_vtb_step_2_desktop`
                                    : fromBank === 'МТС Банк'
                                    ? `payment_guide_${instructionsPrefix}_mts_step_2_desktop`
                                    : isAlfaVnd
                                    ? `payment_guide_${instructionsPrefix}_alfa_step_2_desktop`
                                    : `payment_guide_${instructionsPrefix}_step_2_desktop`
                                }
                                components={{
                                  bold: <Text as='span' fontWeight='900' />,
                                }}
                              />
                            </Text>
                            <Box
                              position='absolute'
                              left='calc(90% + 2px)'
                              top='50%'
                              transform='translateY(-50%)'
                              w='24px'
                              h='24px'
                              pointerEvents='none'
                              display={
                                (isVietnam || isPhilippines || isBrazil) &&
                                !isMobile &&
                                !isSimpleLayout
                                  ? 'block'
                                  : 'none'
                              }
                            >
                              <ArrowRightIcon
                                color='grey.primaryText'
                                boxSize='32px'
                              />
                            </Box>
                          </HStack>
                          <Box mb='10px'>
                            <Text
                              fontWeight='400'
                              fontSize={['md', 'xl']}
                              color='grey.primaryText'
                              lineHeight='1.4'
                            >
                              3.{' '}
                              <Trans
                                ns='payments'
                                i18nKey={`payment_guide_${instructionsPrefix}_step_3_desktop`}
                                components={{
                                  bold: <Text as='span' fontWeight='900' />,
                                }}
                              />
                            </Text>
                          </Box>
                          <Text
                            fontWeight='400'
                            fontSize={['md', 'xl']}
                            color='grey.primaryText'
                            lineHeight='1.4'
                            mt={['16px', '24px']}
                          >
                            4.{' '}
                            <Trans
                              ns='payments'
                              i18nKey={`payment_guide_${instructionsPrefix}_step_4_desktop`}
                              components={{
                                bold: <Text as='span' fontWeight='900' />,
                              }}
                            />
                          </Text>
                        </VStack>
                        <Box
                          flexShrink={0}
                          w={[
                            '100px',
                            isSimpleLayout && !isMobile ? '180px' : '245px',
                          ]}
                          maxW='100%'
                          position='relative'
                        >
                          {!isSimpleLayout || isMobile ? (
                            <Box
                              as='img'
                              src={bankConfig?.qrImageUrl}
                              alt='QR frame'
                              w='100%'
                              h='auto'
                              display='block'
                              userSelect='none'
                            />
                          ) : null}
                          <Box
                            position={
                              isSimpleLayout && !isMobile
                                ? 'relative'
                                : 'absolute'
                            }
                            // NOTE: tune these values if the template image changes.
                            top={isSimpleLayout && !isMobile ? 'auto' : '11%'}
                            left={isSimpleLayout && !isMobile ? 'auto' : '65%'}
                            transform={
                              isSimpleLayout && !isMobile
                                ? 'none'
                                : 'translateX(-50%)'
                            }
                            zIndex={1}
                            display='flex'
                            justifyContent='center'
                            alignItems='center'
                          >
                            <BankDeeplinkQR
                              id={id}
                              fromBank={fromBank ?? null}
                              method={method}
                              value={credentials.value}
                              postProcessStatus={credentials.postProcessStatus}
                              size={isSimpleLayout && !isMobile ? 180 : 155}
                            />
                          </Box>
                        </Box>
                      </>
                    )}
                  </Flex>
                  {isBrazil && fromBank === 'Сбербанк' ? (
                    <HStack
                      w='100%'
                      bg='warning.background'
                      borderWidth='1px'
                      borderColor='warning.button'
                      borderRadius='10px'
                      p={['10px 12px', '12px 16px']}
                      gap='10px'
                      alignItems='center'
                      mt={['16px', '20px']}
                    >
                      <AlertTriangleIcon
                        width='22px'
                        height='22px'
                        stroke='warning.text'
                        flexShrink={0}
                      />
                      <Text
                        fontWeight='700'
                        fontSize={['md', 'lg']}
                        color='warning.text'
                        lineHeight='1.4'
                        textAlign='start'
                      >
                        {t('bra_tg_qr_vpn_hint')}
                      </Text>
                    </HStack>
                  ) : null}
                  {!isMobile ? (
                    <>
                      <Divider
                        borderColor='grey.border'
                        h='1px'
                        my='10px'
                        w='100%'
                      />
                      {isVietnam && (fromBank === 'Сбербанк' || isAlfaVnd) ? (
                        <VndTgQrErrorBanner
                          hasDeeplink={hasDeeplinks}
                          isMobile={isMobile}
                          fromBank={fromBank ?? null}
                          errorImage={qrErrorImageUrl}
                          descriptionKey={
                            isAlfaVnd
                              ? 'vnd_tg_qr_error_description_alfa'
                              : undefined
                          }
                        />
                      ) : isPhilippines && fromBank !== 'Газпромбанк' ? (
                        <PhpTgQrErrorBanner
                          hasDeeplink={hasDeeplinks}
                          isMobile={isMobile}
                          fromBank={fromBank ?? null}
                        />
                      ) : isBrazil ? (
                        <BraTgQrErrorBanner
                          hasDeeplink={hasDeeplinks}
                          isMobile={isMobile}
                          fromBank={fromBank ?? null}
                        />
                      ) : null}
                    </>
                  ) : null}
                </Box>
              )}

              {isVndAn ? (
                <>
                  <HStack
                    justifyContent={
                      showInstructions ? 'space-between' : 'center'
                    }
                    alignItems='center'
                    mb={['16px', '20px']}
                  >
                    {showInstructions && (
                      <Button
                        variant='paymentOutline'
                        borderRadius='8px'
                        justifyContent='center'
                        width='80%'
                        onClick={instructionsDisclosure.onOpen}
                        fontWeight='semibold'
                        fontSize={['sm', 'md']}
                      >
                        {t('payment_instructions')}
                      </Button>
                    )}
                    <Box bg='payment.timeBar' borderRadius='13px' p='10px 20px'>
                      {!(paid || status === 'PAID') && (
                        <Text
                          fontSize={['lg', 'xl']}
                          color='payment.timeBarText'
                          fontWeight='medium'
                        >
                          {timeLeft}
                        </Text>
                      )}
                    </Box>
                  </HStack>
                  <VndAnInstructions
                    amount={amount}
                    currency={currency}
                    accountNumber={credentials.value ?? ''}
                    swiftCode={
                      vndBankSwiftCodes[credentials.bank] ?? credentials.bank
                    }
                    recipientName={credentials.name}
                    bankLogo={
                      isMobile && vndAnBankConfig
                        ? {
                            url: vndAnBankConfig.payImageUrl,
                            alt: vndAnBankConfig.alt,
                          }
                        : undefined
                    }
                    bankLink={
                      vndAnBankLink
                        ? {
                            labelKey: 'vnd_an_open_bank_sber',
                            onClick: () => {
                              trackEvent(
                                AnalyticsEvents.PAYMENT_BANK_PAY_LINK_CLICKED,
                                {
                                  order_id: id,
                                  payment_method: method ?? '',
                                  amount_base: amount,
                                  bank: fromBank ?? undefined,
                                },
                              );
                              try {
                                safeLs?.setItem(
                                  vndAnBankLinkStorageKey,
                                  'true',
                                );
                              } catch {
                                // The link is still hidden for this page view
                              }
                              setIsVndAnBankLinkUsed(true);
                              openDeeplink(vndAnBankLink);
                            },
                          }
                        : undefined
                    }
                    isBranded={isBranded}
                    onInputCopied={onInputCopied}
                  />
                </>
              ) : null}

              {showChatwoot ? (
                <HStack
                  w='100%'
                  bg='payment.qrBackground'
                  borderWidth='1px'
                  borderColor='payment.qrBorder'
                  borderRadius='999px'
                  p='10px 16px'
                  justifyContent='center'
                  alignItems='center'
                  gap='8px'
                  mb='12px'
                  display={['flex', 'none']}
                >
                  <Text
                    fontSize='sm'
                    fontWeight='semibold'
                    color={isBranded ? 'white' : 'payment.titleText'}
                  >
                    {t('payment_chat_hint')}
                  </Text>
                  <ChatwootIcon boxSize='18px' flexShrink={0} />
                </HStack>
              ) : null}
              {/* VND_TG_AN shows its timer above the instructions */}
              {!isVndAn ? (
                <HStack
                  justifyContent='space-between'
                  alignItems='center'
                  mb='5px'
                >
                  {showInstructions && (
                    <Button
                      variant='paymentOutline'
                      borderRadius='8px'
                      justifyContent='center'
                      width='80%'
                      display={['block', 'block']}
                      onClick={instructionsDisclosure.onOpen}
                      fontWeight='semibold'
                      fontSize={['sm', 'md']}
                    >
                      {t('payment_instructions')}
                    </Button>
                  )}
                  <Box bg='payment.timeBar' borderRadius='13px' p='10px 20px'>
                    {!(paid || status === 'PAID') && (
                      <Text
                        fontSize={['lg', 'xl']}
                        color='payment.timeBarText'
                        fontWeight='medium'
                      >
                        {timeLeft}
                      </Text>
                    )}
                  </Box>
                </HStack>
              ) : null}

              {/* VND_TG_AN is paid in rubles, the amount is copied from its instructions */}
              {!isVndAn ? (
                <VStack
                  gap='0'
                  spacing={0}
                  w='100%'
                  align='stretch'
                  mb={['0', '0']}
                >
                  <PaymentFieldRow
                    label={t('payment_amount')}
                    value={`${crossborderAmount ?? ''} ${
                      crossborderCurrency ?? ''
                    }`}
                    onCopyFunc={() => onInputCopied('amount')}
                  />
                </VStack>
              ) : null}
            </Box>
            {showChatwoot ? (
              <HStack
                w='100%'
                bg='payment.qrBackground'
                borderWidth='1px'
                borderColor='payment.qrBorder'
                borderRadius='8px'
                p='10px 16px'
                justifyContent='center'
                alignItems='center'
                gap='8px'
                display={['none', 'flex']}
              >
                <Text
                  fontSize='sm'
                  fontWeight='semibold'
                  color={isBranded ? 'white' : 'payment.titleText'}
                >
                  {t('payment_chat_hint')}
                </Text>
                <ChatwootIcon boxSize='18px' flexShrink={0} />
              </HStack>
            ) : null}
            {/* Controls */}
            <Box bottom='0' mb='0' w='100%' pt={['12px', '15px']}>
              {renderControls()}
            </Box>
          </VStack>
        ) : (
          <HStack
            align='stretch'
            spacing='0'
            gap={['20px', '10px']}
            w='100%'
            flexDir={['column', 'row']}
          >
            {/* Left section: Header with amount, QR, and button */}
            <VStack
              align={['start', 'stretch']}
              alignSelf={['stretch', 'stretch']}
              justify={['flex-start', 'space-between']}
              flexShrink={0}
              w={['100%', 'auto']}
              spacing={['20px', credentials.type === 'QR' ? '20px' : '16px']}
            >
              <VStack pt={['0', '12px']} gap={['12px', '8px']} w='100%'>
                {isMobile ? null : (
                  <Text
                    as='h3'
                    fontSize={['20px', '20px']}
                    fontWeight='medium'
                    variant='numeric'
                    color='payment.titleText'
                    lineHeight='1'
                  >
                    {t('payment_credentials_title')}
                  </Text>
                )}
                <Text
                  as='h1'
                  fontSize={['72px', '64px']}
                  fontWeight='medium'
                  variant='numeric'
                  color='grey.primaryText'
                  lineHeight='1'
                >
                  {amount}
                  <sub style={{ bottom: 0 }}>{currency}</sub>
                </Text>
              </VStack>
              {credentials.type === 'QR' || !isMobile || alfaTjsDeeplink ? (
                <VStack textAlign='center' w={['100%', 'auto']}>
                  {credentials.type === 'QR' ? (
                    isUpi && credentials.value ? (
                      <>
                        <PaymentPageQr value={credentials.value} />
                        <Text
                          fontSize='md'
                          fontWeight='medium'
                          color='grey.tertiaryText'
                          w={['100%', '360px']}
                          display='block'
                        >
                          {t('pay_qr_upi_scan')}
                        </Text>
                        {isMobile ? (
                          <UpiDeeplinkButtons
                            id={id}
                            method={method}
                            amount={amount}
                            value={credentials.value}
                          />
                        ) : null}
                      </>
                    ) : isMobile &&
                      method === 'NSPK_QR' &&
                      credentials.value ? (
                      <Button
                        w='100%'
                        h='56px'
                        variant='payment'
                        borderRadius='8px'
                        onClick={() => openDeeplink(credentials.value ?? '')}
                      >
                        <HStack
                          h='100%'
                          alignItems='center'
                          justifyContent='center'
                          spacing='8px'
                        >
                          <Text
                            color='white'
                            fontSize='lg'
                            fontWeight='semibold'
                          >
                            {t('pay_nspk')}
                          </Text>
                          <ArrowRightIcon
                            color='white'
                            boxSize='20px'
                            flexShrink={0}
                          />
                        </HStack>
                      </Button>
                    ) : (
                      <>
                        {isMobile && hasDeeplinks ? (
                          <BankDeeplinkButtons
                            amount={amount}
                            fromBank={fromBank ?? null}
                            method={method}
                            credentials={credentials}
                            id={id}
                          />
                        ) : null}
                        {isMobile &&
                        method === 'KGS_ELQR' &&
                        credentials.value ? (
                          <ElqrDeeplinkButtons
                            id={id}
                            method={method}
                            amount={amount}
                            value={credentials.value}
                          />
                        ) : null}
                        <Flex
                          w={['180px', '200px']}
                          h={['180px', '200px']}
                          bg={['transparent', 'payment.qrBackground']}
                          borderWidth={isBranded ? '1px' : '0'}
                          borderColor='payment.qrBorder'
                          borderRadius='13px'
                          justifyContent='center'
                          alignItems='center'
                          mx={['auto', '0']}
                        >
                          <BankDeeplinkQR
                            id={id}
                            fromBank={fromBank ?? null}
                            method={method}
                            value={credentials.value}
                            postProcessStatus={credentials.postProcessStatus}
                            size={isMobile ? 180 : 200}
                          />
                        </Flex>
                        {!(isVietnam || isPhilippines || isBrazil) &&
                          !(method === 'NSPK_QR' && isMobile) && (
                            <Text
                              fontSize='md'
                              fontWeight='medium'
                              color='grey.tertiaryText'
                              w={['100%', '360px']}
                              display='block'
                            >
                              {t(isPix ? 'pay_qr_pix' : 'pay_qr')}
                            </Text>
                          )}
                      </>
                    )
                  ) : method &&
                    isDeeplinkMethod(method) &&
                    !isMobcomMethod(method) &&
                    method !== 'UZS_TG' &&
                    method !== 'TJS_TG' &&
                    method !== 'ALFA_ALFA' ? (
                    <>
                      <Flex
                        w={['180px', '200px']}
                        h={['180px', '200px']}
                        bg={['transparent', 'payment.qrBackground']}
                        borderWidth={isBranded ? '1px' : '0'}
                        borderColor='payment.qrBorder'
                        borderRadius='13px'
                        justifyContent='center'
                        alignItems='center'
                        mx={['auto', '0']}
                      >
                        <BankDeeplinkQR
                          id={id}
                          fromBank={fromBank ?? null}
                          method={method}
                          // IDR_TG_WALLET: QR leads to the deeplink page, not the phone
                          value={
                            method === 'IDR_TG_WALLET'
                              ? undefined
                              : credentials.value
                          }
                          postProcessStatus={credentials.postProcessStatus}
                          size={isMobile ? 180 : 200}
                        />
                      </Flex>
                      <Text
                        fontSize='md'
                        fontWeight='medium'
                        color='grey.tertiaryText'
                        w={['100%', '360px']}
                        display='block'
                      >
                        {t('pay_qr')}
                      </Text>
                    </>
                  ) : upiPayLink ? (
                    <>
                      <PaymentPageQr value={upiPayLink} />
                      <Text
                        fontSize='md'
                        fontWeight='medium'
                        color='grey.tertiaryText'
                        w={['100%', '360px']}
                        display='block'
                      >
                        {t('pay_qr_upi')}
                      </Text>
                    </>
                  ) : method === 'TJS_TG' ? (
                    <>
                      <PaymentPageQr
                        value={alfaTjsDeeplink ?? gazpromTjsDeeplink}
                      />
                      <Text
                        fontSize='md'
                        fontWeight='medium'
                        color='grey.tertiaryText'
                        w={['100%', '360px']}
                        display='block'
                      >
                        {t(
                          fromBank === 'Альфа-Банк'
                            ? 'pay_qr_tjs_alfa'
                            : 'pay_qr',
                        )}
                      </Text>
                    </>
                  ) : (
                    <>
                      <Flex
                        w={['180px', '200px']}
                        h={['180px', '200px']}
                        bg={['transparent', 'payment.qrBackground']}
                        borderRadius='13px'
                        justifyContent='center'
                        alignItems='center'
                        mx={['auto', '0']}
                      >
                        <CloudTransferIcon
                          cloudColor='payment.cloudColor'
                          arrowColor='payment.arrowColor'
                        />
                      </Flex>
                      <Text
                        fontSize='md'
                        fontWeight='medium'
                        color='grey.tertiaryText'
                        w={['100%', '360px']}
                        display='block'
                      >
                        {t('pay_transfer')}
                      </Text>
                    </>
                  )}
                </VStack>
              ) : null}
              {showInstructions && (
                <Button
                  w='100%'
                  h='40px'
                  variant='paymentOutline'
                  borderRadius='8px'
                  justifyContent='center'
                  display={['none', 'block']}
                  onClick={instructionsDisclosure.onOpen}
                >
                  {t('payment_instructions')}
                </Button>
              )}
              {/* Deep link buttons - shown on mobile for AMD_TG_SBP, hidden on desktop, but not shown when credentials.type === 'QR' */}
              {isMobile &&
                method &&
                isDeeplinkMethod(method) &&
                credentials.type !== 'QR' && (
                  <Box w='100%'>
                    <BankDeeplinkButtons
                      amount={amount}
                      fromBank={fromBank ?? null}
                      method={method}
                      credentials={credentials}
                      id={id}
                    />
                  </Box>
                )}
            </VStack>

            {/* Right section: Payment details */}
            <VStack
              w='100%'
              align='stretch'
              px={['0', '10px']}
              spacing={['20px', '15px']}
              justify={['space-between', 'space-between']}
              minH={['auto', '100%']}
            >
              <Box>
                <HStack justifyContent='space-between' alignItems='center'>
                  <Text fontWeight='semibold' fontSize='xl'>
                    {t(
                      isMobcomMethod(method)
                        ? 'payment_details_mobcom'
                        : 'payment_details',
                    )}
                  </Text>
                  <Box bg='payment.timeBar' borderRadius='13px' p='10px 20px'>
                    {!(paid || status === 'PAID') && (
                      <Text
                        fontSize='xl'
                        color='payment.timeBarText'
                        fontWeight='medium'
                      >
                        {timeLeft}
                      </Text>
                    )}
                  </Box>
                </HStack>

                {/* IDR_TG_WALLET copies its details from the instruction steps */}
                {method !== 'IDR_TG_WALLET' ? (
                  <VStack
                    gap='0'
                    spacing={0}
                    w='100%'
                    align='stretch'
                    mb={['0', '0']}
                  >
                    <PaymentFieldRow
                      label={t('payment_amount')}
                      value={
                        crossborderAmount && crossborderCurrency
                          ? `${crossborderAmount} ${crossborderCurrency}`
                          : `${amount} ${currency}`
                      }
                      onCopyFunc={() => onInputCopied('amount')}
                    />
                    {credentials.type !== 'QR' ||
                    (isPix && credentials.value) ? (
                      <>
                        <Divider />
                        <PaymentFieldRow
                          label={credentialLabel}
                          value={credentialCopyValue}
                          displayedValue={
                            isPix && credentials.value
                              ? `${credentials.value.slice(
                                  0,
                                  12,
                                )}…${credentials.value.slice(-8)}`
                              : credentials.value ?? ''
                          }
                          customClass='ph-no-capture'
                          onCopyFunc={() => onInputCopied('credentials')}
                        />
                      </>
                    ) : null}
                    {credentials.bank &&
                      !(isVietnam || isPhilippines || isBrazil) &&
                      method !== 'NSPK_QR' &&
                      !isPix && (
                        <>
                          <Divider />
                          <PaymentFieldRow
                            label={t(
                              isMobcomMethod(method)
                                ? 'payment_operator'
                                : 'payment_bank',
                            )}
                            value={credentials.bank}
                            onCopyFunc={() => onInputCopied('bank')}
                          />
                        </>
                      )}
                    {credentials.accountNumber && (
                      <>
                        <Divider />
                        <PaymentFieldRow
                          label={t('payment_account_number')}
                          value={credentials.accountNumber}
                          onCopyFunc={() => onInputCopied('credentials')}
                        />
                      </>
                    )}
                    {credentials.name && method !== 'NSPK_QR' && (
                      <>
                        <Divider />
                        <PaymentFieldRow
                          label={t('payment_recipient')}
                          value={credentials.name}
                          onCopyFunc={() => onInputCopied('recipient')}
                        />
                      </>
                    )}
                    {method === 'SBER_AZN' && (
                      <>
                        <Divider />
                        <PaymentFieldRow
                          label={t('payment_sender_sber')}
                          value=''
                          displayedValue={<ErrorIndicator />}
                          showCopy={false}
                        />
                      </>
                    )}
                  </VStack>
                ) : null}

                {method === 'IDR_TG_WALLET' ? (
                  <IdrWalletInstructions
                    amount={amount}
                    currency={currency}
                    phone={credentials.value ?? ''}
                    bank={credentials.bank}
                    isBranded={isBranded}
                    onInputCopied={onInputCopied}
                  />
                ) : null}

                {/* Payment instructions button - shown on mobile, hidden on desktop */}
                {showInstructions && (
                  <Button
                    w='100%'
                    h='40px'
                    variant='paymentOutline'
                    borderRadius='8px'
                    justifyContent='center'
                    display={['block', 'none']}
                    onClick={instructionsDisclosure.onOpen}
                    mt={['20px', '0']}
                    mb={[isMobcomMethod(method) ? '0' : '20px', '0']}
                  >
                    {t('payment_instructions')}
                  </Button>
                )}

                {/* Payment notice */}
                <Box>
                  <PaymentNotice
                    method={method}
                    fromBank={fromBank}
                    credentials={credentials}
                    style={style}
                  />
                </Box>

                {method === 'MOBCOM_TJS' && !isMobile ? (
                  <HStack
                    w='100%'
                    bg='warning.background'
                    borderWidth='1px'
                    borderColor='warning.button'
                    borderRadius='10px'
                    p='12px 16px'
                    gap='10px'
                    alignItems='flex-start'
                    mt='16px'
                  >
                    <AlertTriangleIcon
                      width='22px'
                      height='22px'
                      stroke='warning.text'
                      flexShrink={0}
                    />
                    <VStack align='start' spacing='6px'>
                      <Text
                        fontWeight='700'
                        fontSize='md'
                        color='warning.text'
                        lineHeight='1.4'
                        textAlign='start'
                      >
                        {t('mobcom_tjs_sber_only_title')}
                      </Text>
                      <Text
                        fontWeight='400'
                        fontSize='sm'
                        color='warning.text'
                        lineHeight='1.4'
                        textAlign='start'
                      >
                        {t('mobcom_tjs_sber_only_description')}
                      </Text>
                    </VStack>
                  </HStack>
                ) : null}
              </Box>
              {showChatwoot ? (
                <HStack
                  w='100%'
                  bg='payment.qrBackground'
                  borderWidth='1px'
                  borderColor='payment.qrBorder'
                  borderRadius='8px'
                  p='10px 16px'
                  justifyContent='center'
                  alignItems='center'
                  gap='8px'
                >
                  <Text
                    fontSize='sm'
                    fontWeight='semibold'
                    color={isBranded ? 'white' : 'payment.titleText'}
                  >
                    {t('payment_chat_hint')}
                  </Text>
                  <ChatwootIcon boxSize='18px' flexShrink={0} />
                </HStack>
              ) : null}
              {/* Controls */}
              <Box
                bottom='0'
                mb='0'
                w='100%'
                pt={isMobcomMethod(method) ? '0' : '15px'}
              >
                {renderControls()}
              </Box>
            </VStack>
          </HStack>
        )}
      </Card>
      <PaymentUploadModal
        isOpen={uploadModalIsOpen}
        onClose={uploadModalOnClose}
        onUploadClick={onUploadClick}
        isLoading={isUploadLoading}
        onUpload={onUploadComplete}
      />
      <PaymentInstructionsModal
        isOpen={instructionsDisclosure.isOpen}
        onClose={instructionsDisclosure.onClose}
        depositId={id}
        method={method}
        credentials={credentials}
        fromBank={fromBank}
        amount={
          crossborderAmount && crossborderCurrency && !isRubGuide
            ? crossborderAmount
            : amount
        }
        currency={
          crossborderAmount && crossborderCurrency && !isRubGuide
            ? crossborderCurrency
            : currency
        }
      />
    </VStack>
  );

  return content;
}
